import React from "react";
import { ColumnsType } from "antd/es/table";
import type { SortOrder } from "antd/es/table/interface";
import { format } from "date-fns";
import { TFunction } from "i18next";

import {
  CombinationRuleOrderBy,
  ICombinationRule,
} from "~/entity/CombinationRules";

/** Колонки таблицы в порядке по дизайну. */
export const combinationRulesTableColumns = [
  "mainPosition",
  "additionalPositions",
  "maxPerEmployee",
  "role",
  "spread",
  "conditions",
  "createdAt",
  "createdBy",
  "updatedAt",
  "updatedBy",
];

/** Сортируемые колонки (по enum orderBy бэкенда). */
export const combinationRulesSortableColumns = [
  "mainPosition",
  "createdAt",
  "createdBy",
  "updatedAt",
  "updatedBy",
];

/** Маппинг колонки таблицы на поле orderBy бэкенда. */
export const combinationRulesSortFieldForApi = (
  option: string,
): CombinationRuleOrderBy => {
  switch (option) {
    case "mainPosition":
      return "mainPositionId";
    case "status":
      return "isActive";
    default:
      return option as CombinationRuleOrderBy;
  }
};

export const getCombinationRulesSortableApiKeys = (): string[] =>
  Array.from(
    new Set(
      combinationRulesSortableColumns.map(combinationRulesSortFieldForApi),
    ),
  );

export const formatRuleDate = (value: string | null): string =>
  value ? format(new Date(value), "dd.MM.yyyy") : "";

/** Уникальные дилеры из всех групп правила, различаются по dealerId. */
export const getRuleDealerNames = (rule: ICombinationRule): string[] => {
  const dealerIds: number[] = [];
  const names: string[] = [];
  (rule.groups ?? []).forEach((group) => {
    (group.dealers ?? []).forEach(({ dealerId, dealerName }) => {
      if (!dealerIds.includes(dealerId)) {
        dealerIds.push(dealerId);
        names.push(dealerName || `#${dealerId}`);
      }
    });
  });
  return names;
};

/** Уникальные типы условий из всех групп правила. */
export const getRuleConditionTypes = (rule: ICombinationRule): string[] => {
  const types: string[] = [];
  (rule.groups ?? []).forEach((group) => {
    (group.conditions ?? []).forEach(({ conditionType }) => {
      if (conditionType && !types.includes(conditionType)) {
        types.push(conditionType);
      }
    });
  });
  return types;
};

/** Текстовое значение ячейки — общая логика для таблицы и CSV-выгрузки. */
export const getRuleCellText = (
  t: TFunction,
  rule: ICombinationRule,
  column: string,
  positionsMap: Map<number, string>,
): string => {
  const positionName = (id: number) => positionsMap.get(id) ?? `#${id}`;

  switch (column) {
    case "mainPosition":
      return positionName(rule.mainPositionId);
    case "additionalPositions":
      return (rule.additionalPositionIds ?? []).map(positionName).join(", ");
    case "maxPerEmployee":
      return rule.maxPerEmployee != null ? String(rule.maxPerEmployee) : "";
    case "role":
      return (rule.rolePositionIds ?? []).map(positionName).join(", ");
    case "spread":
      return getRuleDealerNames(rule).join(", ");
    case "conditions":
      return getRuleConditionTypes(rule)
        .map((type) => t(`combinationRules.conditionTypes.${type}`))
        .join("\n");
    case "createdAt":
      return formatRuleDate(rule.createdAt);
    case "createdBy":
      return rule.createdBy ?? "";
    case "updatedAt":
      return formatRuleDate(rule.updatedAt);
    case "updatedBy":
      return rule.updatedBy ?? "";
    default:
      return "";
  }
};

interface ColumnsArgs {
  positionsMap: Map<number, string>;
  sortField?: string;
  sortType?: string;
}

export const getCombinationRulesColumns = (
  t: TFunction,
  { positionsMap, sortField, sortType }: ColumnsArgs,
): ColumnsType<ICombinationRule> =>
  combinationRulesTableColumns.map((column) => {
    const columnSortable = combinationRulesSortableColumns.includes(column);
    const apiKey = combinationRulesSortFieldForApi(column);
    const sortOrder: SortOrder | undefined =
      columnSortable && sortField === apiKey
        ? sortType === "ASC"
          ? "ascend"
          : sortType === "DESC"
            ? "descend"
            : undefined
        : undefined;

    return {
      title: t(`combinationRules.columns.${column}`),
      dataIndex: column,
      key: columnSortable ? apiKey : column,
      sorter: columnSortable,
      sortOrder,
      render: (_value: unknown, record: ICombinationRule) => (
        <span style={{ whiteSpace: "pre-line" }}>
          {getRuleCellText(t, record, column, positionsMap)}
        </span>
      ),
    };
  });
